<!--
 * @Description: 手风琴式日程页面
 * @Author: AI
 * @Date: 2026-05-23
 -->
<template>
    <div class="schedule-accordion">
        <div class="loading" v-if="loading">
            <van-loading color="#3b82f6" vertical size="30px" text-size="16px">加载中...</van-loading>
        </div>
        <div v-else>
            <template v-if="$i18n.locale === 'zh-CN'">
                <van-collapse v-model="activeNames" accordion @change="onCollapseChange">
                    <van-collapse-item
                        v-for="item in datas"
                        :key="item.roomid"
                        :name="item.roomid"
                        :border="true"
                    >
                        <template #title>
                            <div class="panel-header">
                                <div class="panel-header-left">
                                    <div class="panel-header-time">
                                        <span v-if="isAllDates" class="panel-date"><van-icon name="calendar-o" class="panel-icon" />{{ item.prodate }}</span>
                                        <span class="panel-time"><van-icon name="clock-o" class="panel-icon" />{{ item.starttime }} - {{ item.endtime }}</span>
                                    </div>
                                    <div class="panel-header-venue">
                                        <span class="panel-venue" :title="item.roomname"><van-icon name="location-o" class="panel-icon-venue" />{{ item.roomname }}</span>
                                    </div>
                                </div>
                                <div class="panel-header-right">
                                    <span v-if="item.roomurl" class="panel-live-badge" @click.stop>
                                        <a :href="item.roomurl" target="_top">直播</a>
                                    </span>
                                </div>
                            </div>
                            <div class="panel-topic"><van-icon name="label-o" class="panel-icon-topic" /><span class="panel-topic-text" :title="item.roomtitle">{{ item.roomtitle }}</span></div>
                        </template>

                        <div class="panel-body">
                            <div class="loading" v-if="expandedLoading[item.roomid]">
                                <van-loading color="#3b82f6" vertical size="24px" text-size="14px">加载中...</van-loading>
                            </div>
                            <div v-else-if="expandedEmpty[item.roomid]" class="no-data">暂无数据</div>
                            <template v-else>
                                <div
                                    v-for="(schedule, sIdx) in expandedData[item.roomid]"
                                    :key="sIdx"
                                    class="schedule-block"
                                    :style="{ borderLeftColor: subScheduleColors[sIdx % subScheduleColors.length] }"
                                >
                                    <div class="schedule-time">{{ schedule.startime }} ~ {{ schedule.endtime }}</div>
                                    <div class="schedule-topic" v-html="nToBr(schedule.topic)"></div>
                                    <div class="schedule-experts" v-if="schedule.exData.length > 0">
                                        <div v-for="(expert, eIdx) in schedule.exData" :key="eIdx" class="expert-group">
                                            <span class="expert-role">{{ expert.rolename }}</span>
                                            <span
                                                v-for="(name, nIdx) in expert.exname"
                                                :key="nIdx"
                                                class="expert-name"
                                                @click.stop="todetails(expert.exid[nIdx])"
                                            >{{ name }}</span>
                                        </div>
                                    </div>
                                    <div v-if="schedule.children.length > 0" class="schedule-children">
                                        <div
                                            v-for="(child, cIdx) in schedule.children"
                                            :key="cIdx"
                                            class="schedule-child"
                                        >
                                            <div class="schedule-time-child">{{ child.startime }} ~ {{ child.endtime }}</div>
                                            <div class="schedule-topic-child" v-html="nToBr(child.topic)"></div>
                                            <div class="schedule-experts" v-if="child.exData.length > 0">
                                                <div v-for="(expert, eIdx) in child.exData" :key="eIdx" class="expert-group">
                                                    <span class="expert-role">{{ expert.rolename }}</span>
                                                    <span
                                                        v-for="(name, nIdx) in expert.exname"
                                                        :key="nIdx"
                                                        class="expert-name"
                                                        @click.stop="todetails(expert.exid[nIdx])"
                                                    >{{ name }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </van-collapse-item>
                </van-collapse>
            </template>
            <template v-else>
                <van-collapse v-model="activeNames" accordion @change="onCollapseChange">
                    <van-collapse-item
                        v-for="item in datas"
                        :key="item.roomid"
                        :name="item.roomid"
                        :border="true"
                    >
                        <template #title>
                            <div class="panel-header">
                                <div class="panel-header-left">
                                    <div class="panel-header-time">
                                        <span v-if="isAllDates" class="panel-date"><van-icon name="calendar-o" class="panel-icon" />{{ item.prodate }}</span>
                                        <span class="panel-time"><van-icon name="clock-o" class="panel-icon" />{{ item.starttime }} - {{ item.endtime }}</span>
                                    </div>
                                    <div class="panel-header-venue">
                                        <span class="panel-venue" :title="item.roomenname || item.roomname"><van-icon name="location-o" class="panel-icon-venue" />{{ item.roomenname || item.roomname }}</span>
                                    </div>
                                </div>
                                <div class="panel-header-right">
                                    <span v-if="item.roomurl" class="panel-live-badge" @click.stop>
                                        <a :href="item.roomurl" target="_top">Live</a>
                                    </span>
                                </div>
                            </div>
                            <div class="panel-topic"><van-icon name="label-o" class="panel-icon-topic" /><span class="panel-topic-text" :title="item.roomentitle || item.roomtitle">{{ item.roomentitle || item.roomtitle }}</span></div>
                        </template>

                        <div class="panel-body">
                            <div class="loading" v-if="expandedLoading[item.roomid]">
                                <van-loading color="#3b82f6" vertical size="24px" text-size="14px">Loading...</van-loading>
                            </div>
                            <div v-else-if="expandedEmpty[item.roomid]" class="no-data">No Data</div>
                            <template v-else>
                                <div
                                    v-for="(schedule, sIdx) in expandedData[item.roomid]"
                                    :key="sIdx"
                                    class="schedule-block"
                                    :style="{ borderLeftColor: subScheduleColors[sIdx % subScheduleColors.length] }"
                                >
                                    <div class="schedule-time">{{ schedule.startime }} ~ {{ schedule.endtime }}</div>
                                    <div class="schedule-topic" v-html="nToBr(schedule.entopic || schedule.topic)"></div>
                                    <div class="schedule-experts" v-if="schedule.exData.length > 0">
                                        <div v-for="(expert, eIdx) in schedule.exData" :key="eIdx" class="expert-group">
                                            <span class="expert-role">{{ expert.roleenname || expert.rolename }}</span>
                                            <span
                                                v-for="(name, nIdx) in expert.exenname"
                                                :key="nIdx"
                                                class="expert-name"
                                                @click.stop="todetails(expert.exid[nIdx])"
                                            >{{ name || expert.exname[nIdx] }}</span>
                                        </div>
                                    </div>
                                    <div v-if="schedule.children.length > 0" class="schedule-children">
                                        <div
                                            v-for="(child, cIdx) in schedule.children"
                                            :key="cIdx"
                                            class="schedule-child"
                                        >
                                            <div class="schedule-time-child">{{ child.startime }} ~ {{ child.endtime }}</div>
                                            <div class="schedule-topic-child" v-html="nToBr(child.entopic || child.topic)"></div>
                                            <div class="schedule-experts" v-if="child.exData.length > 0">
                                                <div v-for="(expert, eIdx) in child.exData" :key="eIdx" class="expert-group">
                                                    <span class="expert-role">{{ expert.roleenname || expert.rolename }}</span>
                                                    <span
                                                        v-for="(name, nIdx) in expert.exenname"
                                                        :key="nIdx"
                                                        class="expert-name"
                                                        @click.stop="todetails(expert.exid[nIdx])"
                                                    >{{ name || expert.exname[nIdx] }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </van-collapse-item>
                </van-collapse>
            </template>
        </div>
        <div class="modal" v-if="modalShow">
            <div class="details-conss">
                <DetailsNews :exid="exid" :introStatus="introShow" :taskRoute="detailRoute" @func="closeGuest" />
            </div>
        </div>
    </div>
</template>

<script>
import DetailsNews from './details-news.vue';

export default {
    name: 'scheduleAccordion',
    props: ['introShow'],
    components: {
        DetailsNews,
    },

    data() {
        return {
            loading: false,
            datas: [],
            allDatas: [],
            m: '',
            dateid: 0,
            roomid: 0,
            search: '',
            dateids: 0,
            roomids: 0,
            language: 0,
            activeNames: 0,
            expandedData: {},
            expandedLoading: {},
            expandedEmpty: {},
            modalShow: false,
            exid: null,
            num: 0,
        }
    },

    computed: {
        isAllDates() {
            return !this.dateid || this.dateid === 0 || this.dateid === '0';
        },
        subScheduleColors() {
            return ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];
        },
        detailRoute() {
            return 'accordion';
        },
    },

    watch: {
        $route(to, from) {
            if (to.query.dateid != from.query.dateid && to.query.dateid) {
                this.dateid = to.query.dateid;
                this.activeNames = 0;
                this.expandedData = {};
                this.expandedLoading = {};
                this.expandedEmpty = {};
                this.init();
            } else if (to.query.roomid != from.query.roomid) {
                // roomid变化：过滤列表并展开对应会场
                this.filterByRoomid();
            }
        },
    },

    created() {
        // PC端打开则跳转PC日程页面
        if (!this.isMobile()) {
            const _params = window.location.href.split('?')[1];
            window.location.href = `https://mp.weiqihd.com/pweb/#/schedules?${_params}`;
            return;
        }
        this.language = this.$route.query.language;
        if (this.$route.query) {
            this.dateid = this.$route.query.dateid;
            this.init();
        }
    },

    methods: {
        async init() {
            try {
                this.loading = true;
                const {
                    m,
                    dateid = this.dateid,
                    search = this.search,
                } = this.$route.query;
                this.m = m;

                const _params = Object.assign({ m, dateid },
                    search ? { search } : {}
                );

                const res = await this.$http.initScheduleInfo(_params);
                this.$emit('searchDatas', res.childens);
                this.datas = res.RoomDateEntityList;
                this.allDatas = [...(res.RoomDateEntityList || [])];
                if (res.RoomDateEntityList && res.RoomDateEntityList.length > 0) {
                    this.dateids = res.RoomDateEntityList[0].dateid;
                    this.roomids = res.RoomDateEntityList[0].roomid;
                }
                this.datas.forEach(item => {
                    if (this.language == 1) {
                        if (item.prodate && item.prodate.indexOf('月') != -1) {
                            item.prodate = item.prodate.replace('月', '.');
                        }
                        if (item.prodate && item.prodate.indexOf('日') != -1) {
                            item.prodate = item.prodate.replace('日', ' ');
                        }
                    }
                });
                this.loading = false;

                // 如果URL中有roomid参数，过滤并只显示该会场
                const queryRoomid = this.$route.query.roomid;
                if (queryRoomid && queryRoomid != 0) {
                    this.filterByRoomid();
                }
            } catch (error) {
                console.error(error);
            }
        },

        filterByRoomid() {
            const queryRoomid = this.$route.query.roomid;
            if (queryRoomid && queryRoomid != 0) {
                const rid = Number(queryRoomid);
                this.datas = this.allDatas.filter(item => item.roomid === rid);
                if (this.datas.length > 0) {
                    this.activeNames = rid;
                    this.$nextTick(() => {
                        if (!this.expandedData[rid]) {
                            this.fetchRoomSchedule(rid, 0);
                        }
                    });
                }
            } else {
                // 恢复全部会场
                this.datas = [...this.allDatas];
                this.activeNames = 0;
            }
        },

        onCollapseChange(activeNames) {
            if (!activeNames || activeNames.length === 0) return;
            const roomid = Array.isArray(activeNames) ? activeNames[0] : activeNames;
            if (this.expandedData[roomid]) return;

            const roomIndex = this.datas.findIndex(item => item.roomid === roomid);
            if (roomIndex === -1) return;

            this.fetchRoomSchedule(roomid, roomIndex);
        },

        async fetchRoomSchedule(roomid, index) {
            try {
                this.$set(this.expandedLoading, roomid, true);
                this.$set(this.expandedEmpty, roomid, false);

                const room = this.datas[index];
                const _params = {
                    m: this.m,
                    dateid: room.dateid || this.dateids,
                    roomid: roomid,
                };
                if (this.search) {
                    _params.search = this.search;
                }

                const res = await this.$http.initScheduleInfo(_params);

                if (res.DateListEntity != null && res.DateListEntity.length > 0) {
                    const _list = res.DateListEntity[0].programListEntity[0].programs.programList;
                    this.num = 0;
                    const _data = this.dealData(0, _list);
                    const processed = this.getSubAgendaData(_data);

                    for (let i = 0; i < _data.length; i++) {
                        processed[i].children = this.getSubAgendaData(_data[i].children);
                    }

                    this.$set(this.expandedData, roomid, processed);
                    this.$set(this.expandedEmpty, roomid, false);
                } else {
                    this.$set(this.expandedData, roomid, []);
                    this.$set(this.expandedEmpty, roomid, true);
                }

                this.$set(this.expandedLoading, roomid, false);
            } catch (error) {
                console.error(error);
                this.$set(this.expandedLoading, roomid, false);
                this.$set(this.expandedEmpty, roomid, true);
            }
        },

        convertTime(time) {
            let end = time.split('(')[1].split(')')[0];
            let date = new Date(Number(end));
            let h = date.getHours();
            h = h < 10 ? '0' + h : h;
            let m = date.getMinutes();
            m = m < 10 ? '0' + m : m;
            return h + ':' + m;
        },

        dealData(firstId, data) {
            let _temp = [];
            for (let i = 0; i < data.length; i++) {
                if (data[i].upproid === firstId) {
                    this.num++;
                    _temp.push(data.splice(i, 1)[0]);
                    i--;
                }
            }
            for (let i = 0; i < _temp.length; i++) {
                _temp[i].children = this.dealData(_temp[i].proid, data);
            }
            return _temp;
        },

        getSubAgendaData(data) {
            let SubAgendaData = [];
            for (let k = 0; k < data.length; k++) {
                let endtime = data[k].endtime,
                    startime = data[k].starttime,
                    children = data[k].children,
                    exData = [],
                    exenname = [],
                    exid = [],
                    roleenname = '';
                let exnameId = [];
                for (let s = 0; s < data[k].expertList.length; s++) {
                    exid.push(data[k].expertList[s].exid);
                    exenname.push(data[k].expertList[s].exenname);
                    roleenname = data[k].expertList[s].exrolemodel.roleenname;
                    exnameId.push({
                        exname: data[k].expertList[s].exname,
                        exid: data[k].expertList[s].exid,
                        excompany: data[k].expertList[s].excompany,
                    });
                    exData = this.filterRolename(data[k].expertList);
                }

                SubAgendaData.push({
                    children,
                    startime,
                    endtime,
                    topic: data[k].topic,
                    exid,
                    exData,
                    entopic: data[k].entopic,
                    exenname,
                    roleenname,
                });
            }
            return SubAgendaData;
        },

        filterRolename(data) {
            let _rolenameList = [],
                _rolename = [];

            for (let i in data) {
                _rolename.push(data[i].exrolemodel.rolename);
                let _indexof = _rolenameList.indexOf(_rolename[i]);
                if (_indexof == -1) {
                    _rolenameList.push(data[i].exrolemodel.rolename);
                }
            }
            let _identityList = [];
            _rolenameList.forEach((item) => {
                let _identity = { rolename: '', exname: [], exid: [], excompany: [], exenname: [], exencompany: [] };
                let _exname = [],
                    _exid = [],
                    _excompany = [],
                    _exenname = [],
                    _exencompany = [];
                for (let n in data) {
                    if (item === data[n].exrolemodel.rolename) {
                        _exname.push(data[n].exname);
                        _identity.rolename = item;
                        _identity.exname = _exname;
                        _exid.push(data[n].exid);
                        _identity.exid = _exid;
                        _excompany.push(data[n].excompany);
                        _identity.excompany = _excompany;
                        _exenname.push(data[n].exenname);
                        _identity.exenname = _exenname;
                        _exencompany.push(data[n].exencompany);
                        _identity.exencompany = _exencompany;
                    }
                }
                _identityList.push(_identity);
            });

            return _identityList;
        },

        todetails(param) {
            this.modalShow = true;
            this.exid = param;
        },

        closeGuest() {
            this.modalShow = false;
        },

        nToBr(str) {
            if (str) {
                return str.replace(/\\n/g, '<br/>');
            } else {
                return str;
            }
        },

        isMobile() {
            const ua = navigator.userAgent;
            return /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i.test(ua);
        },
    },
}
</script>

<style lang="scss" scoped>
.schedule-accordion {
    margin-bottom: 80px;
    font-size: 26px;
    line-height: 1.5;
    padding: 0 20px 20px;

    ::v-deep .van-collapse-item {
        margin-top: 20px;
        background: #fff;
        border-radius: 12px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
        overflow: hidden;

        .van-cell {
            padding: 24px 20px;
            align-items: flex-start;
        }

        .van-cell__title {
            flex: 1;
            overflow: hidden;
            min-width: 0;
        }

        .van-cell__right-icon {
            align-self: center;
            color: #64748b;
            font-size: 30px;
        }

        .van-collapse-item__content {
            padding: 0 20px 24px;
            background: #fafbfc;
        }
    }

    ::v-deep .van-hairline--top-bottom::after {
        border-width: 0;
    }

    .panel-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        width: 100%;

        .panel-header-left {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .panel-header-time {
            display: flex;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;

            .panel-date {
                font-size: 28px;
                font-weight: 600;
                color: #1e293b;
                display: flex;
                align-items: center;
                gap: 6px;
            }

            .panel-time {
                font-size: 26px;
                color: #64748b;
                display: flex;
                align-items: center;
                gap: 6px;
            }
        }

        .panel-header-venue {
            .panel-venue {
                font-size: 28px;
                font-weight: 500;
                color: #334155;
                max-width: 400px;
                display: inline-flex;
                align-items: center;
                gap: 8px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        .panel-header-right {
            flex-shrink: 0;
            margin-left: 12px;
        }
    }

    .panel-icon {
        font-size: 28px;
        color: #3b82f6;
        flex-shrink: 0;
    }

    .panel-icon-venue {
        font-size: 30px;
        color: #3b82f6;
        flex-shrink: 0;
    }

    .panel-icon-topic {
        font-size: 28px;
        color: #3b82f6;
        flex-shrink: 0;
        margin-right: 6px;
    }

    .panel-live-badge {
        display: inline-block;
        padding: 4px 14px;
        background: linear-gradient(135deg, #3b82f6, #6366f1);
        border-radius: 20px;
        font-size: 22px;

        a {
            color: #fff;
            text-decoration: none;
        }
    }

    .panel-topic {
        margin-top: 12px;
        display: flex;
        align-items: flex-start;
        line-height: 1.4;

        .panel-icon-topic {
            margin-right: 8px;
            margin-top: 2px;
            flex-shrink: 0;
        }

        .panel-topic-text {
            font-size: 30px;
            font-weight: 600;
            color: #1e293b;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            word-break: break-all;
            flex: 1;
            min-width: 0;
        }
    }

    .panel-body {
        .no-data {
            text-align: center;
            color: #94a3b8;
            padding: 40px 0;
            font-size: 28px;
        }
    }

    .schedule-block {
        padding: 20px 16px;
        margin-bottom: 16px;
        background: #f8fafc;
        border-left: 5px solid;
        border-radius: 0 8px 8px 0;

        &:last-child {
            margin-bottom: 0;
        }

        .schedule-time {
            font-size: 26px;
            color: #3b82f6;
            font-weight: 500;
            margin-bottom: 8px;
        }

        .schedule-topic {
            font-size: 28px;
            color: #1e293b;
            font-weight: 500;
            margin-bottom: 10px;
            line-height: 1.5;
        }

        .schedule-experts {
            .expert-group {
                margin-bottom: 6px;
                line-height: 1.6;
            }

            .expert-role {
                font-size: 24px;
                color: #64748b;
                margin-right: 8px;
            }

            .expert-name {
                font-size: 24px;
                color: #3b82f6;
                margin-right: 12px;
                cursor: pointer;
                display: inline-block;

                &:after {
                    content: '、';
                    color: #94a3b8;
                }

                &:last-child:after {
                    content: '';
                }
            }
        }

        .schedule-children {
            margin-top: 12px;
        }

        .schedule-child {
            margin-top: 10px;
            padding: 14px 12px;
            background: rgba(255, 255, 255, 0.7);
            border-radius: 8px;
            border: 1px solid #e2e8f0;

            .schedule-time-child {
                font-size: 24px;
                color: #3b82f6;
                margin-bottom: 6px;
            }

            .schedule-topic-child {
                font-size: 26px;
                color: #334155;
                margin-bottom: 8px;
                line-height: 1.4;
            }
        }
    }

    .modal {
        position: fixed;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 1000;

        .details-conss {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 90%;
            height: 80%;
            background: #fff;
            overflow-y: auto;
            border-radius: 12px;
        }
    }
}
</style>
